import React from 'react';
import { Field } from '@ark-ui/react';
import clsx from 'clsx';
import styles from './Input.module.scss';

export type InputSize = 'sm' | 'md' | 'lg';

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Label displayed above the input */
  label?: string;
  /** Secondary label text (e.g. optional marker) */
  labelSecondary?: string;
  /** Hint text displayed below the input */
  hint?: string;
  /** Error message — also sets invalid ARIA state */
  error?: string;
  /** Icon placed on the leading (left) edge */
  iconLeft?: React.ReactNode;
  /** Icon placed on the trailing (right) edge */
  iconRight?: React.ReactNode;
  /** Visual size of the input */
  size?: InputSize;
  /** Required test anchor for Playwright and unit tests */
  'data-testid': string;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      labelSecondary,
      hint,
      error,
      iconLeft,
      iconRight,
      size = 'lg',
      className,
      id,
      disabled,
      'data-testid': testId,
      ...inputProps
    },
    ref,
  ) => {
    const isInvalid = !!error;

    return (
      <Field.Root
        invalid={isInvalid}
        disabled={disabled}
        className={clsx(styles.root, className)}
      >
        {label &&
          (id ? (
            // When an explicit id is provided, use a native label so htmlFor/id
            // association is predictable regardless of Ark UI's internal ID generation.
            <label htmlFor={id} className={styles.label}>
              {label}
              {labelSecondary && (
                <span className={styles.labelSecondary}>{labelSecondary}</span>
              )}
            </label>
          ) : (
            <Field.Label className={styles.label}>
              {label}
              {labelSecondary && (
                <span className={styles.labelSecondary}>{labelSecondary}</span>
              )}
            </Field.Label>
          ))}

        <div
          className={clsx(
            styles.inputWrapper,
            styles[`inputWrapper--${size}`],
            isInvalid && styles['inputWrapper--error'],
            disabled && styles['inputWrapper--disabled'],
          )}
        >
          {iconLeft && (
            <span className={styles.iconLeft} aria-hidden="true">
              {iconLeft}
            </span>
          )}

          <Field.Input
            ref={ref}
            id={id}
            data-testid={testId}
            className={clsx(
              styles.input,
              iconLeft && styles['input--with-icon-left'],
              iconRight && styles['input--with-icon-right'],
            )}
            {...inputProps}
          />

          {iconRight && (
            <span className={styles.iconRight} aria-hidden="true">
              {iconRight}
            </span>
          )}
        </div>

        {error ? (
          <Field.ErrorText
            className={clsx(styles.helperText, styles['helperText--error'])}
          >
            {error}
          </Field.ErrorText>
        ) : hint ? (
          <Field.HelperText className={styles.helperText}>
            {hint}
          </Field.HelperText>
        ) : null}
      </Field.Root>
    );
  },
);

Input.displayName = 'Input';
